import { Injectable } from '@angular/core';

type ClarityFn = ((...args: unknown[]) => void) & { q?: unknown[][] };

/**
 * Injects the Microsoft Clarity tracking snippet at runtime.
 *
 * The project id is supplied by the caller (from the active environment), so this
 * service stays decoupled from the app's environment files. When the id is empty
 * — as it is for local/dev environments — initialisation is a no-op, which is how
 * Clarity is kept off outside production/staging/uat.
 *
 * Mirrors the official snippet from https://clarity.microsoft.com.
 */
@Injectable({
  providedIn: 'root',
})
export class ClarityService {
  private initialized = false;

  init(projectId: string | null | undefined): void {
    if (this.initialized || !projectId) {
      return;
    }

    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    this.initialized = true;

    const win = window as unknown as { clarity?: ClarityFn };
    if (!win.clarity) {
      const fn = function (...args: unknown[]) {
        (fn.q = fn.q || []).push(args);
      } as ClarityFn;
      win.clarity = fn;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.clarity.ms/tag/' + projectId;

    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  }
}
